import React, { useState, useEffect } from 'react';
import { therapyApi } from '../../services/api';
import { invalidateLookupsByPrefix } from '../../services/lookupCache';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiX, HiCheckCircle } from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';

const TherapyTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    fee: '',
    color: '#3B82F6',
    is_active: true
  });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await therapyApi.getTypes();
      setTypes(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch therapy types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      duration_minutes: 60,
      fee: '',
      color: '#3B82F6',
      is_active: true
    });
    setShowForm(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      duration_minutes: type.duration_minutes || 60,
      fee: type.fee || '',
      color: type.color || '#3B82F6',
      is_active: type.is_active === 1 || type.is_active === true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const parsedDuration = parseInt(formData.duration_minutes, 10);
      if (!Number.isInteger(parsedDuration) || parsedDuration <= 0) {
        toast.error('Duration must be a positive number');
        return;
      }

      const submitData = {
        ...formData,
        fee: formData.fee ? parseFloat(formData.fee) : null,
        duration_minutes: parsedDuration
      };

      if (editingType) {
        await therapyApi.updateType(editingType.id, submitData);
        toast.success('Therapy type updated successfully');
      } else {
        await therapyApi.createType(submitData);
        toast.success('Therapy type created successfully');
      }
      invalidateLookupsByPrefix('therapyTypes:');
      setShowForm(false);
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save therapy type');
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await therapyApi.deleteType(id);
          invalidateLookupsByPrefix('therapyTypes:');
          toast.success('Therapy type deleted successfully');
          fetchTypes();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete therapy type');
        }
      }
    });
  };

  const handleToggleActive = async (type) => {
    try {
      await therapyApi.updateType(type.id, {
        is_active: !(type.is_active === 1 || type.is_active === true)
      });
      toast.success('Therapy type updated successfully');
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update therapy type');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  // Separate active and inactive types
  const activeTypes = types.filter(type => type.is_active === 1 || type.is_active === true);
  const inactiveTypes = types.filter(type => !(type.is_active === 1 || type.is_active === true));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Therapy Types Configuration</h1>
          <p className="text-gray-500 mt-1">Manage therapy types and their settings</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiPlus className="w-5 h-5" />
          Add Therapy Type
        </button>
      </div>

      {/* Active Therapy Types */}
      {activeTypes.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Therapy Types</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTypes.map((type) => (
              <div
                key={type.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: type.color || '#3B82F6' }}
                    />
                    <h4 className="font-semibold text-gray-900">{type.name}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg"
                      title="Edit"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(type.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {type.description && (
                  <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <span className="ml-1 font-medium">{type.duration_minutes || 60} min</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fee:</span>
                    <span className="ml-1 font-medium">₹{parseFloat(type.fee || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleToggleActive(type)}
                    className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-1"
                  >
                    <HiCheckCircle className="w-4 h-4 text-green-500" />
                    Active
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inactive Therapy Types */}
      {inactiveTypes.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inactive Therapy Types</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inactiveTypes.map((type) => (
              <div
                key={type.id}
                className="p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-75"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: type.color || '#3B82F6' }}
                    />
                    <h4 className="font-semibold text-gray-700">{type.name}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg"
                      title="Edit"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(type.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {type.description && (
                  <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <span className="ml-1 font-medium">{type.duration_minutes || 60} min</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fee:</span>
                    <span className="ml-1 font-medium">₹{parseFloat(type.fee || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleToggleActive(type)}
                    className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-1"
                  >
                    <HiX className="w-4 h-4 text-gray-400" />
                    Inactive
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {types.length === 0 && (
        <div className="card text-center p-8 text-gray-500">
          No therapy types found. Click "Add Therapy Type" to create one.
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">
                {editingType ? 'Edit Therapy Type' : 'Add Therapy Type'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Occupational Therapy (OT)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Description of the therapy type..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        duration_minutes: value === '' ? '' : parseInt(value, 10)
                      });
                    }}
                    required
                    min="1"
                    step="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee per Session (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.fee}
                    onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="is_active" className="text-sm text-gray-700">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Delete Therapy Type"
        message="Are you sure you want to delete this therapy type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default TherapyTypes;

